/**
 * Helpers for parsing and stripping the credit account
 * deep-link query param (`?creditAccountHash=…`) on the credit report surface.
 *
 * Centralizing the param name here keeps the email-service contract in one
 * place; if the parameter is ever renamed, only this module changes.
 */

/** URL query parameter that carries the SHA-256 hex hash of the target account. */
export const CREDIT_DEEP_LINK_PARAM = 'creditAccountHash';

/**
 * Reads the credit account hash from a `location.search` string.
 * Returns `undefined` when the param is missing, blank, or all-whitespace.
 * The returned value is lower-cased so callers don't have to be tolerant.
 */
export function readCreditAccountHashFromSearch(search: string | null | undefined): string | undefined {
  if (typeof search !== 'string' || search.length === 0) {
    return undefined;
  }
  const params = new URLSearchParams(search.charAt(0) === '?' ? search.slice(1) : search);
  const raw = params.get(CREDIT_DEEP_LINK_PARAM);
  if (raw === null) {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? undefined : trimmed.toLowerCase();
}

/**
 * Returns `search` with the deep-link param removed. The returned value
 * keeps the leading `?` when other params remain, and is empty when no
 * params remain (matching the convention React Router uses).
 */
export function stripCreditAccountHashFromSearch(search: string | null | undefined): string {
  if (typeof search !== 'string' || search.length === 0) {
    return '';
  }
  const params = new URLSearchParams(search.charAt(0) === '?' ? search.slice(1) : search);
  if (!params.has(CREDIT_DEEP_LINK_PARAM)) {
    return search;
  }
  params.delete(CREDIT_DEEP_LINK_PARAM);
  const remaining = params.toString();
  return remaining === '' ? '' : `?${remaining}`;
}

/** Convenience: `true` when a non-empty deep-link hash is present in `search`. */
export function hasCreditAccountDeepLink(search: string | null | undefined): boolean {
  return readCreditAccountHashFromSearch(search) !== undefined;
}
